import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FileText,
  Search,
  Filter,
  Download,
  Clock,
  UserCheck,
  Zap,
  AlertCircle,
  CheckCircle,
  Info,
  XCircle,
  Loader2,
  Server,
  ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { API_URL } from "@/config";
import { useAuth } from "@/contexts/AuthContext";
import { apiCall } from "@/utils/api";
import { toast } from "sonner";

interface AssignmentLog {
  id?: number;
  lead_id: number;
  lead_name?: string;
  user_name: string;
  assigned_at: string;
  error?: string;
}

interface WebhookLog {
  id?: number;
  lead_id: number;
  lead_name?: string;
  timestamp?: string;
  source?: string;
  pipeline_id?: string;
  status_id?: string;
  details?: any;
}

interface CombinedAssignmentLog extends Omit<AssignmentLog, "timestamp"> {
  type: "assignment";
  timestamp: Date;
  user_name: string;
}

interface CombinedWebhookLog extends Omit<WebhookLog, "timestamp"> {
  type: "webhook";
  timestamp: Date;
  user_name?: string; // Optional for webhook logs
}

type CombinedLog = CombinedAssignmentLog | CombinedWebhookLog;

interface LogsData {
  assignment_logs: AssignmentLog[];
  webhook_logs: WebhookLog[];
}

interface LogIconProps {
  type: "assignment" | "webhook";
}

const LogsPage: React.FC = () => {

  const { token } = useAuth();

  const [assignmentLogs, setAssignmentLogs] = useState<AssignmentLog[]>([]);
  const [webhookLogs, setWebhookLogs] = useState<WebhookLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [logTypeFilter, setLogTypeFilter] = useState<string>("all"); // 'all', 'assignment', 'webhook'
  const [visibleLogs, setVisibleLogs] = useState<number>(20); // For simple pagination/infinite scroll

  useEffect(() => {
    const fetchLogs = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const [assignmentLogsData, webhookLogsData] = await Promise.all([
          apiCall<{ success: boolean; logs: AssignmentLog[] }>(token, '/assignments/logs'),
          apiCall<{ success: boolean; logs: WebhookLog[] }>(token, '/webhooks/logs')
        ]);

        setAssignmentLogs(assignmentLogsData.logs || []);
        setWebhookLogs(webhookLogsData.logs || []);

        const totalLogs = (assignmentLogsData.logs?.length || 0) + (webhookLogsData.logs?.length || 0);

      } catch (err) {
        console.error(err);
        const errorMessage = err instanceof Error ? err.message : "Unknown error";
        setError(errorMessage);
        toast.error("Erreur de chargement des logs", {
          description: "Impossible de charger l'historique des événements"
        });
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  const combinedLogs = useMemo<CombinedLog[]>(() => {
    const assignments: CombinedAssignmentLog[] = assignmentLogs.map((log) => ({
      ...log,
      type: "assignment" as const,
      timestamp: new Date(log.assigned_at),
    }));
    const webhooks: CombinedWebhookLog[] = webhookLogs.map((log) => ({
      ...log,
      type: "webhook" as const,
      timestamp: log.timestamp ? new Date(log.timestamp) : new Date(),
    }));

    const validAssignments = assignments.filter(log => !isNaN(log.timestamp.getTime()));
    const validWebhooks = webhooks.filter(log => !isNaN(log.timestamp.getTime()));

    return [...validAssignments, ...validWebhooks].sort(
      (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
    );
  }, [assignmentLogs, webhookLogs]);

  const filteredLogs = useMemo<CombinedLog[]>(() => {
    return combinedLogs.filter((log) => {
      const searchTermLower = searchTerm.toLowerCase();
      const matchesSearch =
        (log.lead_name &&
          log.lead_name.toLowerCase().includes(searchTermLower)) ||
        (log.lead_id &&
          String(log.lead_id).toLowerCase().includes(searchTermLower)) ||
        (log.user_name &&
          log.user_name.toLowerCase().includes(searchTermLower));

      const matchesType = logTypeFilter === "all" || log.type === logTypeFilter;
      return matchesSearch && matchesType;
    });
  }, [combinedLogs, searchTerm, logTypeFilter]);

  const handleShowMore = (): void => {
    setVisibleLogs((prev) => prev + 20);
  };

  const LogIcon: React.FC<LogIconProps> = ({ type }) => {
    if (type === "assignment")
      return <UserCheck className="h-4 w-4 text-blue-600" />;
    if (type === "webhook") return <Zap className="h-4 w-4 text-purple-600" />;
    return <Info className="h-4 w-4 text-gray-600" />;
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading logs...</span>
      </div>
    );

  if (error)
    return (
      <div className="text-red-500 p-4 bg-red-50 border border-red-200 rounded-md">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span className="font-medium">Error loading logs:</span>
        </div>
        <p className="mt-1">{error}</p>
      </div>
    );

  return (
    <div className="mx-auto px-4 py-4 space-y-8 animate-in fade-in duration-700">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-8 rounded-3xl border border-slate-200 shadow-sm relative overflow-hidden group">
        <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity">
          <FileText className="h-32 w-32 rotate-12" />
        </div>
        <div className="z-10">
          <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">
            Journal d'Activité
          </h1>
          <p className="text-slate-500 font-medium mt-1">
            Audit complet des <span className="text-primary font-bold">assignations</span> et des <span className="text-purple-600 font-bold">flux entrants</span>.
          </p>
        </div>
        <div className="z-10 flex gap-3">
          <Button variant="outline" className="rounded-xl font-bold bg-white shadow-sm border-slate-200 hover:bg-slate-50 h-11">
            <Download className="mr-2 h-4 w-4" />
            Exporter CSV
          </Button>
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm rounded-3xl overflow-hidden bg-white">
        <CardHeader className="bg-slate-50/50 border-b border-slate-100 p-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white rounded-2xl shadow-sm border border-slate-100 text-primary">
                <Server className="h-6 w-6" />
              </div>
              <div>
                <CardTitle className="text-xl font-bold text-slate-900">Flux d'Événements</CardTitle>
                <CardDescription className="text-slate-500 font-medium">Analyse temps-réel des interactions système.</CardDescription>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <div className="relative flex-1 sm:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Filtrer Lead ID, Nom..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white border-slate-200 focus:ring-primary/10 transition-all rounded-xl h-11"
                />
              </div>
              <Select value={logTypeFilter} onValueChange={setLogTypeFilter}>
                <SelectTrigger className="w-full sm:w-[200px] h-11 rounded-xl bg-white border-slate-200 font-bold text-slate-600">
                  <Filter className="mr-2 h-4 w-4 opacity-50" />
                  <SelectValue placeholder="Type de log" />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-slate-200">
                  <SelectItem value="all" className="font-medium">Tous les événements</SelectItem>
                  <SelectItem value="assignment" className="font-medium">Assignations</SelectItem>
                  <SelectItem value="webhook" className="font-medium">Webhooks</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredLogs.length === 0 ? (
            <div className="text-center py-24 bg-slate-50/20">
              <div className="bg-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="h-10 w-10 text-slate-300" />
              </div>
              <p className="text-slate-500 font-bold text-lg">Aucun résultat trouvé</p>
              <p className="text-slate-400 text-sm mt-1">Ajustez vos filtres pour voir plus d'événements.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {filteredLogs.slice(0, visibleLogs).map((log, index) => (
                <div
                  key={
                    log.id ||
                    `${log.type}-${log.timestamp && !isNaN(log.timestamp.getTime()) ? log.timestamp.toISOString() : Date.now()}-${index}`
                  }
                  className="flex items-start gap-4 p-6 hover:bg-slate-50/50 transition-all group"
                >
                  <div className={cn(
                    "h-12 w-12 rounded-2xl flex items-center justify-center border transition-all flex-shrink-0 group-hover:scale-110",
                    log.type === "assignment"
                      ? "bg-blue-50 border-blue-100 text-blue-600"
                      : "bg-purple-50 border-purple-100 text-purple-600"
                  )}>
                    <LogIcon type={log.type} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          "px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider",
                          log.type === "assignment" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"
                        )}>
                          {log.type === "assignment" ? "Assignation" : "Webhook"}
                        </span>
                        <span className="text-sm font-black text-slate-900">
                          Lead <span className="text-primary underline decoration-primary/20 decoration-2 underline-offset-4">#{log.lead_id}</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs font-bold text-slate-400">
                        <Clock className="w-3.5 h-3.5 opacity-50" />
                        {log.timestamp ? log.timestamp.toLocaleString("fr-FR", { dateStyle: 'medium', timeStyle: 'short' }) : "Date inconnue"}
                      </div>
                    </div>

                    <p className="text-sm font-medium text-slate-600 leading-relaxed mb-3">
                      {log.type === "assignment"
                        ? <><span className="text-slate-900 font-bold">{log.lead_name || "Lead N/A"}</span> a été dirigé vers <span className="px-2 py-0.5 bg-slate-100 rounded-lg text-slate-900 font-bold">@{log.user_name}</span></>
                        : <><span className="text-slate-900 font-bold">{log.lead_name || "Lead N/A"}</span> : Nouveau flux entrant détecté.</>
                      }
                    </p>

                    {log.type === "webhook" && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 flex flex-col gap-1">
                          <span className="text-[10px] font-black text-slate-400 uppercase">Source</span>
                          <span className="text-xs font-bold text-slate-700 truncate">{log.source || "N/A"}</span>
                        </div>
                        <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 flex flex-col gap-1">
                          <span className="text-[10px] font-black text-slate-400 uppercase">Status ID</span>
                          <span className="text-xs font-bold text-slate-700 truncate">{log.status_id || "N/A"}</span>
                        </div>
                        <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 flex flex-col gap-1">
                          <span className="text-[10px] font-black text-slate-400 uppercase">Pipeline</span>
                          <span className="text-xs font-bold text-slate-700 truncate">{log.pipeline_id || "N/A"}</span>
                        </div>
                      </div>
                    )}

                    {log.type === "assignment" && log.error && (
                      <div className="mt-2 text-xs font-bold text-red-600 bg-red-50 border border-red-100 p-3 rounded-xl flex items-center gap-3">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        {log.error}
                      </div>
                    )}
                  </div>

                  <ArrowRight className="h-5 w-5 text-slate-200 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all flex-shrink-0 mt-3" />
                </div>
              ))}

              {visibleLogs < filteredLogs.length && (
                <div className="p-8 bg-slate-50/30 flex justify-center">
                  <Button
                    variant="outline"
                    onClick={handleShowMore}
                    disabled={loading}
                    className="w-full sm:w-80 h-12 font-bold rounded-xl border-slate-200 hover:bg-white hover:shadow-sm transition-all"
                  >
                    Afficher l'historique plus ancien ({filteredLogs.length - visibleLogs} restants)
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LogsPage;
